/**
 * Cron Jobs Service
 *
 * Service for interacting with the cron job dashboard API endpoints.
 */

import api from "./api";

export interface CronJobHistory {
  lastRun: Date | null;
  lastSuccess: Date | null;
  failures: number;
  consecutiveFailures: number;
}

export interface CronJobMetadata {
  name: string;
  description: string;
  schedule: string;
  cronPattern: string;
  category: string;
  canManualRun?: boolean;
}

export interface CronJobStatus {
  running: boolean;
  history: CronJobHistory;
  metadata: CronJobMetadata;
}

export interface CronJobsStatusResponse {
  success: boolean;
  data: Record<string, CronJobStatus>;
  timestamp: Date;
}

export interface CronHealthReport {
  success: boolean;
  data: {
    timestamp: Date;
    totalJobs: number;
    healthStatus: "healthy" | "warning" | "critical";
    jobs: Record<string, any>;
    alerts: string[];
  };
}

export interface CronOverview {
  success: boolean;
  data: {
    totalJobs: number;
    healthStatus: "healthy" | "warning" | "critical";
    activeJobs: number;
    alerts: string[];
    categories: Record<
      string,
      {
        total: number;
        healthy: number;
        warning: number;
        critical: number;
      }
    >;
    recentActivity: Array<{
      jobName: string;
      name: string;
      lastRun: Date;
      success: boolean;
    }>;
  };
  timestamp: Date;
}

export const cronJobsService = {
  /**
   * Get overview statistics of all cron jobs
   */
  getOverview: async (): Promise<CronOverview> => {
    return await api.get("/cron-jobs/overview");
  },

  /**
   * Get status of all cron jobs
   */
  getAllJobsStatus: async (): Promise<CronJobsStatusResponse> => {
    return await api.get("/cron-jobs/status");
  },

  /**
   * Get detailed health report
   */
  getHealthReport: async (): Promise<CronHealthReport> => {
    return await api.get("/cron-jobs/health");
  },

  /**
   * Get status of a specific job
   */
  getJobStatus: async (jobName: string): Promise<any> => {
    return await api.get(`/cron-jobs/${jobName}`);
  },

  /**
   * Manually trigger a cron job
   */
  triggerJob: async (jobName: string): Promise<any> => {
    return await api.post(`/cron-jobs/${jobName}/run`);
  },

  /**
   * Get execution history summary
   */
  getHistorySummary: async (): Promise<any> => {
    return await api.get("/cron-jobs/history/summary");
  },
};

export default cronJobsService;
